import { Link, useNavigate } from "react-router-dom"; // 🟢 AÑADIDO useNavigate
import { useAuth } from "@/context/AuthContext"; // 🟢 AÑADIDO useAuth
import { useEffect } from "react"; // 🟢 AÑADIDO useEffect
import { 
  Calendar, DollarSign, Scissors, BarChart3, 
  Globe, MessageCircle, ArrowRight, CheckCircle2 
} from "lucide-react";

const LandingPage = () => {
  // 🟢 INICIO DEL AUTO-PILOTO
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Si ya terminó de cargar el contexto y el usuario está logueado, lo mandamos al panel
    if (!loading && isAuthenticated) {
      navigate("/admin", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);
  // 🟢 FIN DEL AUTO-PILOTO

  return (
    <div className="min-h-screen bg-[#09090b] text-white selection:bg-amber-500 selection:text-black font-sans">
      
      {/* 🌟 NAVBAR (Respeta el área segura) */}
      <nav className="fixed top-0 z-50 w-full bg-black/80 backdrop-blur-md border-b border-zinc-800 pt-[max(1rem,env(safe-area-inset-top))] pb-4 px-6">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Boose Enterprise" className="w-10 h-10 object-contain rounded-lg" />
            <span className="font-heading text-xl font-bold uppercase tracking-widest text-white">
              BARBERSHOP<span className="text-amber-500">.</span>
            </span>
          </div>
          <div className="flex gap-4">
            {/* El botón de Iniciar Sesión original (se oculta en celulares) */}
            <Link to="/admin/login" className="hidden sm:flex items-center text-sm font-bold text-zinc-300 hover:text-white transition-colors">
              Iniciar Sesión
            </Link>
            <Link to="/admin/register" className="bg-amber-500 text-black px-6 py-2.5 rounded-lg font-heading uppercase text-sm font-bold hover:bg-amber-400 transition-colors flex items-center">
              Empezar Gratis <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </nav>

      {/* 🚀 HERO SECTION */}
      <main className="relative pt-[calc(11rem+env(safe-area-inset-top))] pb-32 overflow-hidden">
        {/* Luces de fondo (Efecto Glow) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-amber-500/30 text-amber-500 text-xs font-bold uppercase tracking-widest mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            El SaaS Definitivo para Barberías
          </div>
          
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter mb-6 leading-tight">
            Digitaliza tu barbería <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              y multiplica tus ingresos
            </span>
          </h1>
          
          <p className="text-zinc-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Agenda citas por internet, controla tu inventario, analiza tus finanzas y envía recordatorios automáticos por WhatsApp. Todo en un solo lugar.
          </p>
          
          <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md mx-auto">
            <Link to="/admin/register" className="w-full bg-amber-500 text-black px-8 py-4 rounded-xl font-heading uppercase text-lg font-black hover:bg-amber-400 hover:scale-[1.02] transition-all shadow-[0_0_20px_rgba(245,158,11,0.2)]">
              Crea tu cuenta ahora
            </Link>
            
            <Link to="/demo" className="w-full bg-zinc-900 border border-zinc-800 text-white px-8 py-4 rounded-xl font-heading uppercase text-lg font-bold hover:bg-zinc-800 transition-all">
              Ver Demo
            </Link>

            {/* 🟢 NUEVO: Enlace sutil de inicio de sesión para móviles 🟢 */}
            <div className="mt-4 text-center sm:hidden">
              <p className="text-zinc-500 text-sm">
                ¿Ya tienes una cuenta?{' '}
                <Link to="/admin/login" className="text-amber-500 font-bold hover:underline">
                  Inicia sesión aquí
                </Link>
              </p>
            </div>
          </div>
          
          <p className="text-zinc-600 text-sm mt-8 font-medium">No se requiere tarjeta de crédito para registrarse.</p>
        </div>
      </main>

      {/* 🛠️ FEATURES SECTION */}
      <section className="py-24 bg-zinc-950 border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-bold uppercase mb-4">Todo lo que necesitas</h2>
            <p className="text-zinc-400">Diseñado por y para dueños de negocios exigentes.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: "Citas 24/7", desc: "Tus clientes reservan solos a través de tu propio link personalizado." },
              { icon: MessageCircle, title: "WhatsApp Automático", desc: "Reduce las inasistencias con recordatorios que se envían solos." },
              { icon: DollarSign, title: "Control Financiero", desc: "Registra cada ingreso y gasto. Conoce tu ganancia neta al centavo." },
              { icon: BarChart3, title: "Reportes Detallados", desc: "Exporta PDF y Excel de tu rendimiento mensual con un clic." },
              { icon: Scissors, title: "Gestión de Personal", desc: "Asigna cortes a tus barberos y calcula comisiones fácilmente." },
              { icon: Globe, title: "Multi-Moneda", desc: "Cobra en DOP, USD, EUR o MXN. El sistema se adapta a tu país." }
            ].map((feature, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-2xl hover:border-amber-500/50 transition-colors group">
                <feature.icon className="w-10 h-10 text-amber-500 mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 💳 PRICING SECTION */}
      <section className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-bold uppercase mb-4">Precio Simple y Justo</h2>
            <p className="text-zinc-400">Sin comisiones ocultas ni contratos. Cancela cuando quieras.</p>
          </div>

          <div className="max-w-lg mx-auto bg-gradient-to-b from-zinc-900 to-black border border-amber-500/30 rounded-3xl p-8 relative shadow-[0_0_50px_rgba(245,158,11,0.1)]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-amber-500 text-black px-4 py-1 rounded-b-lg font-bold text-xs uppercase tracking-wider">
              Acceso Total
            </div>
            
            <div className="text-center mt-6 mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Plan Pro</h3>
              <div className="flex items-end justify-center gap-1">
                <span className="text-3xl text-zinc-500 font-bold">$</span>
                <span className="text-7xl font-black text-amber-500 tracking-tighter">10</span>
                <span className="text-zinc-500 font-bold mb-2">/mes</span>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {[
                "Citas ilimitadas",
                "Link de reservas propio",
                "Gestión de inventario",
                "Notificaciones de WhatsApp",
                "Soporte técnico prioritario",
                "Reportes en PDF y Excel"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <Link to="/admin/register" className="block w-full text-center bg-amber-500 text-black py-4 rounded-xl font-heading uppercase text-lg font-bold hover:bg-amber-400 transition-colors">
              Comenzar Ahora
            </Link>
          </div>
        </div>
      </section>

      {/* 🏁 FOOTER */}
      <footer className="border-t border-white/10 bg-black py-12 text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src="/logo192.png" alt="Boose Enterprise" className="w-8 h-8 object-contain rounded-md opacity-50 grayscale" />
          <span className="font-heading text-lg font-bold uppercase tracking-widest text-zinc-600">
            BARBERSHOP<span className="text-amber-500/50">.</span>
          </span>
        </div>
        <p className="text-zinc-600 text-sm">
          &copy; {new Date().getFullYear()} Boose Enterprise. Todos los derechos reservados.
        </p>
      </footer>

    </div>
  );
};

export default LandingPage;